import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Trophy, Star, Shield, Bot, TrendingUp, MessageSquare, ChevronRight,
  Award, Crown, Medal, ThumbsUp, Zap, ArrowRight, ExternalLink,
  BarChart3, Users, Activity, Clock, Send, Gem, Flame, Share2, Copy, Check, UserPlus, Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Navbar, Footer } from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const FEEDBACK_TAGS = [
  'accurate', 'fast', 'helpful', 'detailed', 'reliable',
  'insightful', 'clear', 'needs-improvement', 'slow', 'inaccurate',
];

function RankBadge({ rank }) {
  if (rank === 1) return (
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg shadow-yellow-500/20" data-testid="rank-badge-1">
      <Crown className="w-6 h-6 text-white" />
    </div>
  );
  if (rank === 2) return (
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center" data-testid="rank-badge-2">
      <Medal className="w-6 h-6 text-white" />
    </div>
  );
  if (rank === 3) return (
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-700 flex items-center justify-center" data-testid="rank-badge-3">
      <Award className="w-6 h-6 text-white" />
    </div>
  );
  return (
    <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
      <span className="text-lg font-bold text-gray-400">#{rank}</span>
    </div>
  );
}

function ScoreRing({ score, size = 72 }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : '#ef4444';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="rgba(255,255,255,0.05)" strokeWidth="4" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke={color} strokeWidth="4" fill="none"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

function DistributionBar({ distribution }) {
  const buckets = ['0-19', '20-39', '40-59', '60-79', '80-100'];
  const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const total = Object.values(distribution).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-1.5">
      {buckets.map((bucket, i) => {
        const count = distribution[bucket] || 0;
        const pct = (count / total) * 100;
        return (
          <div key={bucket} className="flex items-center gap-2 text-xs">
            <span className="w-12 text-gray-500 text-right tabular-nums">{bucket}</span>
            <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${colors[i]} transition-all duration-700`}
                style={{ width: `${Math.max(pct, pct > 0 ? 4 : 0)}%` }}
              />
            </div>
            <span className="w-6 text-gray-600 tabular-nums">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function FeedbackForm({ agentId, agentName, onSubmitted }) {
  const [score, setScore] = useState(80);
  const [selectedTags, setSelectedTags] = useState([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { token } = useAuth();

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/agents/${agentId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ score, tags: selectedTags, comment: comment || null }),
      });
      if (res.ok) {
        setSubmitted(true);
        onSubmitted?.();
        setTimeout(() => setSubmitted(false), 3000);
        setScore(80);
        setSelectedTags([]);
        setComment('');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
        <ThumbsUp className="w-5 h-5 text-green-400 mx-auto mb-2" />
        <p className="text-sm text-green-400">Feedback submitted for {agentName}!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`feedback-form-${agentId}`}>
      {/* Score slider */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500">Reputation Score</span>
          <span className="text-sm font-bold text-white">{score}/100</span>
        </div>
        <input
          type="range" min="0" max="100" value={score}
          onChange={e => setScore(parseInt(e.target.value))}
          className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-blue-500"
          data-testid={`score-slider-${agentId}`}
        />
      </div>

      {/* Tags */}
      <div>
        <span className="text-xs text-gray-500 block mb-1.5">Tags (optional)</span>
        <div className="flex flex-wrap gap-1.5">
          {FEEDBACK_TAGS.map(tag => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-all ${
                selectedTags.includes(tag)
                  ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                  : 'bg-white/[0.02] border-white/5 text-gray-500 hover:border-white/10'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Optional comment..."
        rows={2}
        className="w-full bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2 text-sm text-gray-300 placeholder-gray-600 resize-none focus:outline-none focus:border-blue-500/30"
        data-testid={`comment-input-${agentId}`}
      />

      <Button
        onClick={handleSubmit}
        disabled={submitting || !token}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 gap-2"
        size="sm"
        data-testid={`submit-feedback-${agentId}`}
      >
        <Send className="w-3.5 h-3.5" />
        {!token ? 'Sign in to Rate' : submitting ? 'Submitting...' : 'Submit Rating'}
      </Button>
    </div>
  );
}

function AgentLeaderboardCard({ agent, onFeedbackSubmitted }) {
  const [expanded, setExpanded] = useState(false);
  const isCredit = agent.type === 'credit_analysis';
  const score = agent.reputation?.score || 0;
  const rank = agent.rank;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (rank - 1) * 0.1 }}
      className={`rounded-2xl border transition-all ${
        rank === 1
          ? 'border-yellow-500/20 bg-gradient-to-br from-yellow-500/[0.04] to-amber-500/[0.02]'
          : 'border-white/5 bg-white/[0.02] hover:border-white/10'
      }`}
      data-testid={`leaderboard-card-${agent.agent_id}`}
    >
      <div className="p-5">
        {/* Header row */}
        <div className="flex items-center gap-4 mb-4">
          <RankBadge rank={rank} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold truncate" data-testid={`agent-name-${agent.agent_id}`}>
                {agent.name}
              </h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                isCredit ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'
              }`}>
                {isCredit ? 'Credit' : 'Bid'}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              <span>v{agent.version}</span>
              <span className="w-1 h-1 rounded-full bg-gray-700" />
              <span>{agent.model}</span>
              <span className="w-1 h-1 rounded-full bg-gray-700" />
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Active
              </span>
            </div>
          </div>

          <ScoreRing score={score} />
        </div>

        <p className="text-sm text-gray-400 leading-relaxed mb-4">{agent.description}</p>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <div className="text-lg font-bold text-white" data-testid={`stat-score-${agent.agent_id}`}>{score}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Reputation</div>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <div className="text-lg font-bold text-white">{agent.reputation?.total_feedback || 0}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Ratings</div>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <div className="text-lg font-bold text-white">{agent.erc8004?.total_analyses?.toLocaleString()}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Analyses</div>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
            <div className="text-lg font-bold text-white">{agent.erc8004?.validations_passed}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Validations</div>
          </div>
        </div>

        {/* Tags */}
        {agent.top_tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {agent.top_tags.map(t => (
              <span key={t.tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/5 text-[11px] text-gray-400">
                {t.tag} <span className="text-gray-600">({t.count})</span>
              </span>
            ))}
          </div>
        )}

        {/* Distribution + Recent Feedback */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
          data-testid={`expand-${agent.agent_id}`}
        >
          {expanded ? 'Collapse Details' : 'View Details & Rate'}
          <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {/* Expandable section */}
      {expanded && (
        <div className="border-t border-white/5 p-5 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            {/* Score distribution */}
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5" /> Score Distribution
              </h4>
              <DistributionBar distribution={agent.score_distribution || {}} />
            </div>

            {/* Recent feedback */}
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Recent Feedback
              </h4>
              {agent.recent_feedback?.length > 0 ? (
                <div className="space-y-2">
                  {agent.recent_feedback.slice(0, 3).map((fb, i) => (
                    <div key={i} className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04] text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-400">{fb.user_name || 'Anonymous'}</span>
                        <span className={`font-bold ${fb.score >= 70 ? 'text-green-400' : fb.score >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {fb.score}/100
                        </span>
                      </div>
                      {fb.comment && <p className="text-gray-500 line-clamp-1">{fb.comment}</p>}
                      {fb.tags?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {fb.tags.map(t => (
                            <span key={t} className="px-1.5 py-0.5 rounded bg-white/[0.03] text-[10px] text-gray-500">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-600 italic">No feedback yet. Be the first to rate!</p>
              )}
            </div>
          </div>

          {/* Feedback form */}
          <div className="border-t border-white/5 pt-4">
            <h4 className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5" /> Rate This Agent
            </h4>
            <FeedbackForm
              agentId={agent.agent_id}
              agentName={agent.name}
              onSubmitted={onFeedbackSubmitted}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function LeaderboardPage() {
  const [data, setData] = useState(null);
  const [ratersData, setRatersData] = useState(null);
  const [rewardsPool, setRewardsPool] = useState(null);
  const [myRewards, setMyRewards] = useState(null);
  const [myReferral, setMyReferral] = useState(null);
  const [referralLeaderboard, setReferralLeaderboard] = useState(null);
  const [ratersTab, setRatersTab] = useState('weekly');
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const { token, isAuthenticated } = useAuth();

  const fetchLeaderboard = useCallback(() => {
    const fetches = [
      fetch(`${API}/api/agents/leaderboard`).then(r => r.json()),
      fetch(`${API}/api/agents/top-raters`).then(r => r.json()),
      fetch(`${API}/api/rewards/pool`).then(r => r.json()),
      fetch(`${API}/api/referral/leaderboard`).then(r => r.json()),
    ];
    if (token) {
      fetches.push(
        fetch(`${API}/api/rewards/my`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/referral/my`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      );
    }
    Promise.all(fetches).then(([lb, raters, pool, refLb, rewards, referral]) => {
      setData(lb);
      setRatersData(raters);
      setRewardsPool(pool);
      setReferralLeaderboard(refLb);
      if (rewards) setMyRewards(rewards);
      if (referral) setMyReferral(referral);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const copyReferralLink = () => {
    if (!myReferral?.referral_code) return;
    const link = `${window.location.origin}/auth?ref=${myReferral.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClaim = async () => {
    if (!token) return;
    setClaiming(true);
    setClaimResult(null);
    try {
      const res = await fetch(`${API}/api/rewards/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setClaimResult({ success: true, ...data });
        fetchLeaderboard();
      } else {
        setClaimResult({ success: false, message: data.detail || 'Failed to claim reward' });
      }
    } catch (e) {
      setClaimResult({ success: false, message: 'Failed to claim reward' });
    } finally {
      setClaiming(false);
    }
  };

  const stats = data?.platform_stats;
  const leaderboard = data?.leaderboard || [];
  const topRaters = ratersTab === 'weekly' ? ratersData?.weekly : ratersData?.alltime;
  const raterStats = ratersData?.stats;
  const pool = rewardsPool?.pool;
  const tiers = rewardsPool?.tiers;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="leaderboard-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
            <Trophy className="w-4 h-4 text-yellow-400" />
            <span className="text-sm text-yellow-300">Community-Ranked</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-5"
          >
            AI Agent{' '}
            <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
              Leaderboard
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-10"
          >
            Ranked by community reputation. Rate the AI agents that analyze your credit and suggest your bids.
            On-chain identity via ERC-8004 ensures every score is transparent and tamper-proof.
          </motion.p>

          {/* Platform stats */}
          {stats && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto"
              data-testid="platform-stats"
            >
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
                <Bot className="w-4 h-4 text-blue-400 mx-auto mb-1.5" />
                <div className="text-xl font-bold">{stats.total_agents}</div>
                <div className="text-[10px] text-gray-500">Active Agents</div>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
                <MessageSquare className="w-4 h-4 text-green-400 mx-auto mb-1.5" />
                <div className="text-xl font-bold">{stats.total_feedback}</div>
                <div className="text-[10px] text-gray-500">Total Ratings</div>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
                <Star className="w-4 h-4 text-yellow-400 mx-auto mb-1.5" />
                <div className="text-xl font-bold">{stats.avg_platform_score}</div>
                <div className="text-[10px] text-gray-500">Avg Score</div>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
                <Shield className="w-4 h-4 text-purple-400 mx-auto mb-1.5" />
                <div className="text-xl font-bold">{stats.standard}</div>
                <div className="text-[10px] text-gray-500">{stats.chain}</div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Leaderboard */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4" data-testid="leaderboard-list">
              {leaderboard.map(agent => (
                <AgentLeaderboardCard
                  key={agent.agent_id}
                  agent={agent}
                  onFeedbackSubmitted={fetchLeaderboard}
                />
              ))}
            </div>
          )}

          {/* Top Raters */}
          {topRaters && topRaters.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden"
              data-testid="top-raters-section"
            >
              {/* Header */}
              <div className="p-5 border-b border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                      <Flame className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Top Community Raters</h3>
                      <p className="text-[10px] text-gray-500">
                        {raterStats?.total_unique_raters || 0} unique raters | {raterStats?.ratings_this_week || 0} ratings this week
                      </p>
                    </div>
                  </div>
                  {/* Tab toggle */}
                  <div className="flex rounded-lg overflow-hidden border border-white/10">
                    <button
                      onClick={() => setRatersTab('weekly')}
                      className={`px-3 py-1.5 text-xs font-medium transition-all ${
                        ratersTab === 'weekly'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-white/[0.02] text-gray-500 hover:text-gray-300'
                      }`}
                      data-testid="raters-tab-weekly"
                    >
                      This Week
                    </button>
                    <button
                      onClick={() => setRatersTab('alltime')}
                      className={`px-3 py-1.5 text-xs font-medium transition-all ${
                        ratersTab === 'alltime'
                          ? 'bg-blue-500/20 text-blue-300'
                          : 'bg-white/[0.02] text-gray-500 hover:text-gray-300'
                      }`}
                      data-testid="raters-tab-alltime"
                    >
                      All Time
                    </button>
                  </div>
                </div>
              </div>

              {/* Raters list */}
              <div className="divide-y divide-white/[0.04]">
                {topRaters.map((rater) => {
                  const tierIcon = {
                    diamond: <Gem className="w-4 h-4" />,
                    gold: <Crown className="w-4 h-4" />,
                    silver: <Medal className="w-4 h-4" />,
                    bronze: <Award className="w-4 h-4" />,
                  }[rater.tier] || <Star className="w-4 h-4" />;

                  return (
                    <div
                      key={rater.user_id}
                      className={`flex items-center gap-4 px-5 py-3.5 transition-colors ${
                        rater.is_top ? 'bg-yellow-500/[0.03]' : 'hover:bg-white/[0.01]'
                      }`}
                      data-testid={`rater-${rater.rank}`}
                    >
                      {/* Rank */}
                      <div className="w-8 text-center">
                        {rater.rank === 1 ? (
                          <span className="text-yellow-400 text-lg font-bold">1</span>
                        ) : rater.rank === 2 ? (
                          <span className="text-gray-300 text-lg font-bold">2</span>
                        ) : rater.rank === 3 ? (
                          <span className="text-orange-400 text-lg font-bold">3</span>
                        ) : (
                          <span className="text-gray-600 text-sm">{rater.rank}</span>
                        )}
                      </div>

                      {/* Avatar */}
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border"
                        style={{
                          borderColor: rater.color + '40',
                          backgroundColor: rater.color + '15',
                          color: rater.color,
                        }}
                      >
                        {rater.user_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>

                      {/* Name & badge */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate" data-testid={`rater-name-${rater.rank}`}>
                            {rater.user_name}
                          </span>
                          {rater.is_top && (
                            <span className="px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-[9px] font-bold uppercase tracking-wider">
                              Top Rater
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5" style={{ color: rater.color }}>
                          {tierIcon}
                          <span className="text-[11px] font-medium">{rater.badge}</span>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="text-right">
                        <div className="text-sm font-bold text-white">{rater.ratings_count}</div>
                        <div className="text-[10px] text-gray-500">ratings</div>
                      </div>
                      <div className="text-right w-16">
                        <div className="text-sm font-medium text-gray-300">{rater.avg_score_given}</div>
                        <div className="text-[10px] text-gray-500">avg score</div>
                      </div>
                      <div className="text-right w-12">
                        <div className="text-sm font-medium text-gray-400">{rater.agents_rated}</div>
                        <div className="text-[10px] text-gray-500">agents</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Badge tiers legend */}
              <div className="p-4 border-t border-white/5 bg-white/[0.01]">
                <div className="flex flex-wrap items-center justify-center gap-4 text-[10px] text-gray-500">
                  <span className="font-medium text-gray-400">Badge Tiers:</span>
                  {ratersData?.stats?.badge_tiers?.map(t => (
                    <span key={t.tier} className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.badge} ({t.min}+ ratings)
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Rater Rewards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 rounded-2xl border border-yellow-500/10 bg-gradient-to-br from-yellow-500/[0.03] to-orange-500/[0.02] overflow-hidden"
            data-testid="rater-rewards-section"
          >
            {/* Header */}
            <div className="p-5 border-b border-yellow-500/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">Rater Rewards Program</h3>
                  <p className="text-[10px] text-gray-500">Earn KOJIRU tokens by rating AI agents every week</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* Reward tiers */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {tiers?.map(t => {
                  const tierColors = {
                    diamond: { bg: 'from-cyan-400/10 to-blue-400/10', border: 'border-cyan-400/20', text: 'text-cyan-300', icon: <Gem className="w-5 h-5" /> },
                    gold: { bg: 'from-yellow-400/10 to-amber-400/10', border: 'border-yellow-400/20', text: 'text-yellow-300', icon: <Crown className="w-5 h-5" /> },
                    silver: { bg: 'from-gray-300/10 to-gray-400/10', border: 'border-gray-300/20', text: 'text-gray-300', icon: <Medal className="w-5 h-5" /> },
                    bronze: { bg: 'from-orange-400/10 to-orange-600/10', border: 'border-orange-400/20', text: 'text-orange-300', icon: <Award className="w-5 h-5" /> },
                  }[t.tier] || {};
                  const isUserTier = myRewards?.current_week?.tier === t.tier;

                  return (
                    <div
                      key={t.tier}
                      className={`relative p-4 rounded-xl bg-gradient-to-br ${tierColors.bg} border ${tierColors.border} text-center transition-all ${isUserTier ? 'ring-1 ring-yellow-400/40' : ''}`}
                      data-testid={`reward-tier-${t.tier}`}
                    >
                      {isUserTier && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-yellow-500 text-[9px] text-black font-bold uppercase">
                          You
                        </div>
                      )}
                      <div className={`${tierColors.text} flex justify-center mb-2`}>{tierColors.icon}</div>
                      <div className="text-xs font-semibold mb-1">{t.label}</div>
                      <div className="text-lg font-bold text-white">{t.weekly_kutro}</div>
                      <div className="text-[10px] text-gray-500">KOJIRU/week</div>
                      <div className="text-[10px] text-gray-600 mt-1">{t.min_ratings}+ ratings</div>
                    </div>
                  );
                })}
              </div>

              {/* Streak Multiplier info */}
              {rewardsPool?.streak_multipliers && (
                <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4" data-testid="streak-multiplier-info">
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className="w-4 h-4 text-orange-400" />
                    <span className="text-xs font-semibold">Streak Multiplier</span>
                    <span className="text-[10px] text-gray-500">— Rate every week to boost your rewards</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {rewardsPool.streak_multipliers.map((s, i) => {
                      const colors = {
                        legendary: 'from-purple-500 to-pink-500 text-white',
                        blazing: 'from-red-500 to-orange-500 text-white',
                        on_fire: 'from-orange-400 to-yellow-400 text-black',
                        heating_up: 'from-yellow-400 to-amber-300 text-black',
                        started: 'from-gray-400 to-gray-500 text-white',
                      }[s.emoji] || 'from-gray-400 to-gray-500 text-white';

                      return (
                        <div
                          key={s.weeks}
                          className={`px-3 py-1.5 rounded-lg bg-gradient-to-r ${colors} text-xs font-bold flex items-center gap-1.5`}
                          data-testid={`streak-tier-${s.emoji}`}
                        >
                          {s.weeks >= 4 && <Flame className="w-3 h-3" />}
                          <span>{s.multiplier}x</span>
                          <span className="font-normal opacity-80">{s.label}</span>
                          <span className="font-normal opacity-60">({s.weeks}w)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pool stats */}
              {pool && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-sm font-bold text-white">{(pool.total_allocated / 1000000).toFixed(0)}M</div>
                    <div className="text-[10px] text-gray-500">Total Pool</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-sm font-bold text-green-400">{pool.total_distributed.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500">Distributed</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-sm font-bold text-yellow-400">{pool.pending_this_week.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500">Pending This Week</div>
                  </div>
                </div>
              )}

              {/* User's reward status */}
              {isAuthenticated && myRewards ? (
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-3" data-testid="my-rewards-status">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-medium text-gray-400">Your Rewards</h4>
                    <span className="text-xs text-gray-500">
                      Total earned: <span className="text-yellow-400 font-bold">{myRewards.total_earned.toLocaleString()} KOJIRU</span>
                    </span>
                  </div>

                  {/* Streak banner */}
                  {myRewards.streak && myRewards.streak.current_streak > 0 && (
                    <div
                      className={`p-3 rounded-xl border text-center ${
                        myRewards.streak.current_streak >= 8 ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20' :
                        myRewards.streak.current_streak >= 4 ? 'bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/20' :
                        myRewards.streak.current_streak >= 3 ? 'bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border-orange-500/20' :
                        myRewards.streak.current_streak >= 2 ? 'bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-yellow-500/20' :
                        'bg-white/[0.02] border-white/5'
                      }`}
                      data-testid="streak-banner"
                    >
                      <div className="flex items-center justify-center gap-2 mb-1">
                        {myRewards.streak.current_streak >= 4 && <Flame className="w-4 h-4 text-red-400" />}
                        {myRewards.streak.current_streak >= 2 && myRewards.streak.current_streak < 4 && <Flame className="w-4 h-4 text-orange-400" />}
                        <span className="text-sm font-bold text-white">
                          {myRewards.streak.current_streak}-Week Streak
                        </span>
                        {myRewards.streak.multiplier > 1 && (
                          <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 text-[11px] font-bold">
                            {myRewards.streak.multiplier}x
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-400">
                        {myRewards.streak.label}
                        {myRewards.streak.multiplier > 1 && ` — earning ${myRewards.streak.multiplier}x rewards!`}
                      </div>

                      {/* Streak progress dots */}
                      <div className="flex items-center justify-center gap-1 mt-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(w => (
                          <div
                            key={w}
                            className={`w-3 h-3 rounded-full transition-all ${
                              w <= myRewards.streak.current_streak
                                ? w >= 8 ? 'bg-purple-400 shadow-md shadow-purple-500/50'
                                : w >= 4 ? 'bg-red-400 shadow-md shadow-red-500/50'
                                : w >= 3 ? 'bg-orange-400'
                                : w >= 2 ? 'bg-yellow-400'
                                : 'bg-green-400'
                                : 'bg-white/10'
                            }`}
                            title={
                              w === 2 ? '1.5x (Heating Up)' :
                              w === 3 ? '1.75x (On Fire)' :
                              w === 4 ? '2x (Blazing)' :
                              w === 8 ? '3x (Legendary)' :
                              `Week ${w}`
                            }
                          />
                        ))}
                      </div>

                      {/* Next multiplier */}
                      {myRewards.streak.next_multiplier && (
                        <div className="text-[10px] text-gray-500 mt-1.5">
                          {myRewards.streak.next_multiplier.weeks_needed} more week{myRewards.streak.next_multiplier.weeks_needed > 1 ? 's' : ''} to{' '}
                          <span className="text-white">{myRewards.streak.next_multiplier.label} ({myRewards.streak.next_multiplier.multiplier}x)</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div>
                      <div className="text-lg font-bold text-white">{myRewards.current_week.ratings}</div>
                      <div className="text-[10px] text-gray-500">Ratings</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold capitalize" style={{
                        color: { diamond: '#b9f2ff', gold: '#ffd700', silver: '#c0c0c0', bronze: '#cd7f32' }[myRewards.current_week.tier] || '#6b7280'
                      }}>
                        {myRewards.current_week.tier_label || 'None'}
                      </div>
                      <div className="text-[10px] text-gray-500">Tier</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-white">
                        {myRewards.streak?.multiplier > 1 ? `${myRewards.streak.multiplier}x` : '1x'}
                      </div>
                      <div className="text-[10px] text-gray-500">Multiplier</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-yellow-400">{myRewards.current_week.pending_reward}</div>
                      <div className="text-[10px] text-gray-500">KOJIRU</div>
                    </div>
                  </div>

                  {/* Base vs boosted reward */}
                  {myRewards.streak?.multiplier > 1 && myRewards.current_week.base_reward && (
                    <div className="text-center text-xs text-gray-500">
                      Base: <span className="line-through">{myRewards.current_week.base_reward}</span>{' '}
                      <ArrowRight className="w-3 h-3 inline" />{' '}
                      Boosted: <span className="text-yellow-400 font-bold">{myRewards.current_week.pending_reward} KOJIRU</span>
                    </div>
                  )}

                  {/* Progress to next tier */}
                  {myRewards.current_week.next_tier && (
                    <div className="px-3 py-2 rounded-lg bg-blue-500/5 border border-blue-500/10 text-xs text-gray-400">
                      <TrendingUp className="w-3 h-3 text-blue-400 inline mr-1.5" />
                      Rate <span className="text-white font-medium">{myRewards.current_week.next_tier.ratings_needed} more</span> agents to reach{' '}
                      <span className="text-white font-medium">{myRewards.current_week.next_tier.label}</span> and earn{' '}
                      <span className="text-yellow-400 font-medium">{myRewards.current_week.next_tier.weekly_kutro} KOJIRU</span>/week
                    </div>
                  )}

                  {/* Claim button */}
                  {myRewards.current_week.pending_reward > 0 && (
                    <div>
                      {claimResult?.success ? (
                        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                          <ThumbsUp className="w-5 h-5 text-green-400 mx-auto mb-1" />
                          <p className="text-sm text-green-400 font-medium">{claimResult.message}</p>
                        </div>
                      ) : claimResult?.success === false ? (
                        <div className="text-center space-y-2">
                          <p className="text-xs text-gray-500">{claimResult.message}</p>
                        </div>
                      ) : (
                        <Button
                          onClick={handleClaim}
                          disabled={claiming}
                          className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:brightness-110 text-black font-semibold gap-2"
                          data-testid="claim-reward-btn"
                        >
                          <Trophy className="w-4 h-4" />
                          {claiming ? 'Claiming...' : `Claim ${myRewards.current_week.pending_reward} KOJIRU`}
                          {myRewards.streak?.multiplier > 1 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-black/20 text-[10px]">
                              {myRewards.streak.multiplier}x streak
                            </span>
                          )}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Reward history */}
                  {myRewards.history?.length > 0 && (
                    <div className="pt-3 border-t border-white/5">
                      <h5 className="text-[10px] font-medium text-gray-500 mb-2 uppercase tracking-wider">Reward History</h5>
                      <div className="space-y-1.5">
                        {myRewards.history.slice(0, 5).map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-white/[0.02]">
                            <div className="flex items-center gap-2">
                              <span className="capitalize text-gray-400">{r.tier_label} Rater</span>
                              {r.multiplier > 1 && (
                                <span className="px-1 py-0.5 rounded bg-yellow-500/10 text-yellow-400 text-[9px] font-bold">{r.multiplier}x</span>
                              )}
                              <span className="text-gray-600">({r.ratings_count} ratings)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-yellow-400 font-medium">+{r.kutro_amount} KOJIRU</span>
                              <span className="text-gray-600 text-[10px]">{r.week_key}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                  <p className="text-sm text-gray-500 mb-3">Sign in to track your rewards and claim KOJIRU tokens</p>
                  <Link to="/auth">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-2">
                      <Users className="w-3.5 h-3.5" /> Sign In
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Referral Program */}
          {isAuthenticated && myReferral && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 rounded-2xl border border-green-500/10 bg-gradient-to-br from-green-500/[0.03] to-emerald-500/[0.02] overflow-hidden"
              data-testid="referral-section"
            >
              <div className="p-5 border-b border-green-500/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">Referral Bonus</h3>
                    <p className="text-[10px] text-gray-500">Earn 10% of your referral's first claim in KOJIRU tokens</p>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Referral link */}
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">Your Referral Link</label>
                  <div className="flex gap-2">
                    <div className="flex-1 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm text-gray-300 font-mono truncate" data-testid="referral-link">
                      {window.location.origin}/auth?ref={myReferral.referral_code}
                    </div>
                    <Button
                      onClick={copyReferralLink}
                      variant="outline"
                      size="sm"
                      className="border-green-500/20 text-green-400 hover:bg-green-500/10 gap-1.5 shrink-0"
                      data-testid="copy-referral-btn"
                    >
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>

                {/* How it works */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                    <Share2 className="w-4 h-4 text-green-400 mx-auto mb-1.5" />
                    <div className="text-[11px] font-medium">Share Link</div>
                    <div className="text-[9px] text-gray-500 mt-0.5">Send to friends</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                    <UserPlus className="w-4 h-4 text-blue-400 mx-auto mb-1.5" />
                    <div className="text-[11px] font-medium">They Sign Up</div>
                    <div className="text-[9px] text-gray-500 mt-0.5">& rate agents</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                    <Gift className="w-4 h-4 text-yellow-400 mx-auto mb-1.5" />
                    <div className="text-[11px] font-medium">Earn 10%</div>
                    <div className="text-[9px] text-gray-500 mt-0.5">of their first claim</div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-lg font-bold text-white">{myReferral.stats.total_referrals}</div>
                    <div className="text-[10px] text-gray-500">Referrals</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-lg font-bold text-green-400">{myReferral.stats.converted}</div>
                    <div className="text-[10px] text-gray-500">Converted</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-lg font-bold text-yellow-400">{myReferral.stats.pending}</div>
                    <div className="text-[10px] text-gray-500">Pending</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                    <div className="text-lg font-bold text-yellow-400">{myReferral.stats.total_bonus_earned}</div>
                    <div className="text-[10px] text-gray-500">KOJIRU Earned</div>
                  </div>
                </div>

                {/* Referral history */}
                {myReferral.referrals.length > 0 && (
                  <div className="space-y-1.5">
                    <h5 className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Your Referrals</h5>
                    {myReferral.referrals.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-2 px-3 rounded bg-white/[0.02] border border-white/[0.04]">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center text-green-400 text-[10px] font-bold">
                            {r.referred_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="text-gray-300">{r.referred_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {r.bonus_paid ? (
                            <span className="flex items-center gap-1 text-green-400">
                              <Check className="w-3 h-3" /> +{r.bonus_amount} KOJIRU
                            </span>
                          ) : (
                            <span className="text-gray-600">Pending first claim</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Top Referrers Leaderboard */}
          {referralLeaderboard && referralLeaderboard.leaderboard?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 rounded-2xl border border-purple-500/10 bg-gradient-to-br from-purple-500/[0.03] to-pink-500/[0.02] overflow-hidden"
              data-testid="top-referrers-section"
            >
              <div className="p-5 border-b border-purple-500/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Top Referrers</h3>
                      <p className="text-[10px] text-gray-500">
                        {referralLeaderboard.stats.total_referrals} total referrals | {referralLeaderboard.stats.conversion_rate}% conversion rate
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-yellow-400">{referralLeaderboard.stats.total_bonus_distributed} KOJIRU</div>
                    <div className="text-[10px] text-gray-500">Total Bonuses Paid</div>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-white/[0.04]">
                {referralLeaderboard.leaderboard.map((referrer) => {
                  const tierIcon = {
                    diamond: <Gem className="w-4 h-4" />,
                    gold: <Crown className="w-4 h-4" />,
                    silver: <Medal className="w-4 h-4" />,
                    bronze: <Award className="w-4 h-4" />,
                  }[referrer.tier] || <Star className="w-4 h-4" />;

                  return (
                    <div
                      key={referrer.user_id}
                      className={`flex items-center gap-4 px-5 py-3.5 transition-colors ${
                        referrer.rank === 1 ? 'bg-purple-500/[0.03]' : 'hover:bg-white/[0.01]'
                      }`}
                      data-testid={`referrer-${referrer.rank}`}
                    >
                      <div className="w-8 text-center">
                        {referrer.rank === 1 ? (
                          <span className="text-purple-400 text-lg font-bold">1</span>
                        ) : referrer.rank === 2 ? (
                          <span className="text-gray-300 text-lg font-bold">2</span>
                        ) : referrer.rank === 3 ? (
                          <span className="text-pink-400 text-lg font-bold">3</span>
                        ) : (
                          <span className="text-gray-600 text-sm">{referrer.rank}</span>
                        )}
                      </div>

                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border"
                        style={{
                          borderColor: referrer.color + '40',
                          backgroundColor: referrer.color + '15',
                          color: referrer.color,
                        }}
                      >
                        {referrer.user_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{referrer.user_name}</span>
                          {referrer.rank === 1 && (
                            <span className="px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-[9px] font-bold uppercase tracking-wider">
                              Top Referrer
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5" style={{ color: referrer.color }}>
                          {tierIcon}
                          <span className="text-[11px] font-medium">{referrer.tier_label}</span>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-sm font-bold text-white">{referrer.total_referrals}</div>
                        <div className="text-[10px] text-gray-500">referrals</div>
                      </div>
                      <div className="text-right w-16">
                        <div className="text-sm font-medium text-green-400">{referrer.converted}</div>
                        <div className="text-[10px] text-gray-500">converted</div>
                      </div>
                      <div className="text-right w-20">
                        <div className="text-sm font-medium text-yellow-400">{referrer.total_bonus}</div>
                        <div className="text-[10px] text-gray-500">KOJIRU earned</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Referrer tier legend */}
              <div className="p-4 border-t border-white/5 bg-white/[0.01]">
                <div className="flex flex-wrap items-center justify-center gap-4 text-[10px] text-gray-500">
                  <span className="font-medium text-gray-400">Referrer Tiers:</span>
                  {referralLeaderboard.tiers?.map(t => (
                    <span key={t.tier} className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.label} ({t.min_referrals}+ referrals)
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Explainer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 rounded-2xl border border-white/5 bg-white/[0.02]"
            data-testid="leaderboard-explainer"
          >
            <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" /> How the Leaderboard Works
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 text-sm font-bold">1</div>
                <h4 className="text-sm font-medium">Use an Agent</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Get a credit analysis or bid suggestion — free or premium via x402 micropayment.
                </p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-400 text-sm font-bold">2</div>
                <h4 className="text-sm font-medium">Rate the Agent</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Score accuracy (0-100), add tags, and leave comments. Your feedback is recorded on-chain via ERC-8004.
                </p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center text-yellow-400 text-sm font-bold">3</div>
                <h4 className="text-sm font-medium">Rankings Update</h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Agents are ranked by average community score. Higher reputation = more trust for your funds.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to Try These AI Agents?</h2>
          <p className="text-sm text-gray-400 mb-8">
            Connect your wallet, get an AI analysis, and rate the experience. Help build trustworthy AI for DeFi.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-purple-500 gap-2" data-testid="leaderboard-cta">
                Launch App <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/agents">
              <Button size="lg" variant="outline" className="border-white/10 gap-2">
                Agent Profiles <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
